import {
  COUNT_VARIANCE_TOLERANCE_DEFAULT,
  type StockMovementKindWire,
  type StockStatus,
} from '@palaccount/contracts';

import { formatQty, formatQtyWithUnit } from '@/lib/format';

/**
 * The stock screens' pure display logic, kept out of the components so the
 * rules the specs argue about are testable on their own:
 *
 *  - BR-INV-05-03: OK renders NO badge. Only low / out / over speak up.
 *  - INV-05 §5.7's variance severity: |variance| 0 green, 1..tolerance amber,
 *    above tolerance red — the tolerance being the contract's
 *    COUNT_VARIANCE_TOLERANCE_DEFAULT, so the boundary lives in one place.
 *  - BR-INV-05-12: variance = system − counted, POSITIVE means MISSING; the
 *    subtraction is exact BigInt work on the 3-decimal strings, never a float
 *    (DECISION.md §2.1 — quantities are strings end to end).
 *  - INV-06 §5.3's eleven kinds and their colours.
 */

// ── Exact 3-decimal arithmetic on quantity strings ──────────────────────────

const QTY_SCALE = 3;

/** "12.5" → 12500n; null when the text is not a quantity. */
export function toMilliQty(value: string): bigint | null {
  const text = value.trim();
  if (!/^-?\d{1,15}(\.\d{1,3})?$/.test(text)) return null;
  const negative = text.startsWith('-');
  const bare = negative ? text.slice(1) : text;
  const [whole = '0', fraction = ''] = bare.split('.');
  const scaled = BigInt(whole) * 1000n + BigInt(fraction.padEnd(QTY_SCALE, '0'));
  return negative ? -scaled : scaled;
}

/** 12500n → "12.5" — trailing zeros are NUMERIC padding, not information. */
export function fromMilliQty(value: bigint): string {
  const negative = value < 0n;
  const digits = (negative ? -value : value).toString().padStart(QTY_SCALE + 1, '0');
  const text = `${digits.slice(0, -QTY_SCALE)}.${digits.slice(-QTY_SCALE)}`.replace(/\.?0+$/, '');
  return `${negative ? '-' : ''}${text === '' ? '0' : text}`;
}

/**
 * BR-INV-05-12 — variance = system − counted. A blank counted quantity means
 * "not counted" and yields null; it is NEVER coerced to zero (BR-INV-05-13).
 */
export function computeVariance(systemQty: string, countedQty: string): string | null {
  if (countedQty.trim() === '') return null;
  const system = toMilliQty(systemQty);
  const counted = toMilliQty(countedQty);
  if (system === null || counted === null) return null;
  return fromMilliQty(system - counted);
}

// ── INV-05 §5.7 — the variance severity colour ──────────────────────────────

export type VarianceSeverity = 'none' | 'match' | 'minor' | 'major';

/**
 * |variance| 0 → match (green), 1..tolerance → minor (amber), above the
 * tolerance → major (red). The boundary is INCLUSIVE: exactly the tolerance
 * is still amber.
 *
 * BR-INV-05-17 is why `tolerance` is a PARAMETER. The prototype compared
 * against a literal 10; here the caller hands in the company's own setting and
 * the default exists only so a call site with no tenant in hand still has an
 * answer. A future edit that inlines the number back into the comparison puts
 * the rule straight back where it was.
 */
export function varianceSeverity(
  variance: string | null,
  tolerance: number = COUNT_VARIANCE_TOLERANCE_DEFAULT,
): VarianceSeverity {
  if (variance === null || variance.trim() === '') return 'none';
  const scaled = toMilliQty(variance);
  if (scaled === null) return 'none';
  const magnitude = scaled < 0n ? -scaled : scaled;
  if (magnitude === 0n) return 'match';
  return magnitude <= BigInt(tolerance) * 1000n ? 'minor' : 'major';
}

export const VARIANCE_CLASSES: Readonly<Record<VarianceSeverity, string>> = {
  none: 'text-muted-foreground',
  match: 'text-emerald-700 dark:text-emerald-400',
  minor: 'text-amber-700 dark:text-amber-400',
  major: 'text-red-600 dark:text-red-400',
};

// ── BR-INV-05-01/-03 — the derived status badge ─────────────────────────────

export interface StockBadge {
  /** The i18n key of the badge's word. */
  key: string;
  className: string;
}

const BADGES: Readonly<Record<Exclude<StockStatus, 'ok'>, StockBadge>> = {
  low: {
    key: 'stock.badgeLow',
    className: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  },
  out: {
    key: 'stock.badgeOut',
    className: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
  },
  over: {
    key: 'stock.badgeOver',
    className: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
  },
};

/** BR-INV-05-03: an OK row carries NO badge — silence is the good news. */
export function stockBadge(status: StockStatus): StockBadge | null {
  return status === 'ok' ? null : BADGES[status];
}

/** The row wash of INV-05 §5.2 — beats the zebra stripe when it applies. */
export function stockRowWash(status: StockStatus): string {
  switch (status) {
    case 'out':
      return 'bg-red-50 dark:bg-red-950/30';
    case 'low':
      return 'bg-amber-50 dark:bg-amber-950/30';
    case 'over':
      return 'bg-blue-50 dark:bg-blue-950/30';
    case 'ok':
      return '';
  }
}

/** Available is coloured only when it is telling you something (INV-05 §5.2). */
export function availableClass(status: StockStatus): string {
  switch (status) {
    case 'out':
      return 'text-red-600 dark:text-red-400';
    case 'low':
      return 'text-amber-700 dark:text-amber-400';
    case 'over':
    case 'ok':
      return '';
  }
}

// ── INV-06 §5.3 — the eleven kinds and their colours ────────────────────────

export const MOVEMENT_KINDS: readonly StockMovementKindWire[] = [
  'sale',
  'purchase',
  'transfer_in',
  'transfer_out',
  'adjustment_in',
  'adjustment_out',
  'stock_take',
  'customer_return',
  'supplier_return',
  'bundle_assembly',
  'bundle_disassembly',
  // PART 9 §9.5 — the two manufacturing movements, so a consumption issue and
  // a finished-goods receipt are filterable in INV-06 like anything else.
  'consumption',
  'production_output',
  // Wave 5C — the value-only cost adjustment (import charges on a receipt).
  'landed_cost',
];

type KindTone = 'red' | 'green' | 'blue' | 'amber' | 'violet';

const KIND_TONES: Readonly<Record<StockMovementKindWire, KindTone>> = {
  sale: 'red',
  purchase: 'green',
  transfer_in: 'blue',
  transfer_out: 'blue',
  adjustment_in: 'green',
  adjustment_out: 'red',
  stock_take: 'amber',
  customer_return: 'green',
  supplier_return: 'red',
  bundle_assembly: 'violet',
  bundle_disassembly: 'violet',
  // Out of the parts shelf, into the finished-goods shelf: the same red/green
  // reading every other pair on this list uses.
  consumption: 'red',
  production_output: 'green',
  // Value moves, units do not — the stock-take amber, "a figure was restated".
  landed_cost: 'amber',
};

const DOT_CLASSES: Readonly<Record<KindTone, string>> = {
  red: 'bg-red-500',
  green: 'bg-emerald-500',
  blue: 'bg-blue-500',
  amber: 'bg-amber-500',
  violet: 'bg-violet-500',
};

const PILL_CLASSES: Readonly<Record<KindTone, string>> = {
  red: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
  green: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  blue: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
  amber: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  violet: 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300',
};

export function movementDotClass(kind: StockMovementKindWire): string {
  return DOT_CLASSES[KIND_TONES[kind]];
}

export function movementPillClass(kind: StockMovementKindWire): string {
  return PILL_CLASSES[KIND_TONES[kind]];
}

/** A signed quantity reads green up, red down (INV-06 §5.2). */
export function signedQtyClass(qty: string): string {
  const scaled = toMilliQty(qty);
  if (scaled === null || scaled === 0n) return '';
  return scaled < 0n ? 'text-red-600 dark:text-red-400' : 'text-emerald-700 dark:text-emerald-400';
}

/**
 * INV-06's signed quantities, written the way `N-4` asks for: the sign, then
 * the decimals the item's unit allows, then the unit.
 *
 * This REPLACES `withSign`, which trimmed ("12.500" → "12.5") and printed no
 * unit — both halves of `N-4` backwards. It is deleted rather than kept beside
 * this one: a trimming signed formatter sitting in the same module is exactly
 * what gets picked up next time somebody needs a signed quantity, and the rule
 * goes back to being unenforced with nobody having decided anything.
 *
 * Both of its call sites are quantities inside ONE unit — the movement row,
 * and INV-06's net card, which `BR-INV-06-15` only populates when every
 * filtered movement shares a unit (`N-14`: "adding 3 kg to 4 boxes is
 * meaningless"). A net across units arrives null and prints nothing at all.
 */
export function signedQtyWithUnit(qty: string, precision: number, unitSymbol: string): string {
  const written = formatQtyWithUnit(qty, precision, unitSymbol);
  // The sign is decided by what is PRINTED, not by the stored value: rounding
  // to the unit's decimals can turn a tiny movement into zero, and "+0" is a
  // claim the column should never make. It also means a value arriving wider
  // than `toMilliQty`'s 3-decimal wire limit still signs correctly.
  const positive = !written.startsWith('-') && /[1-9]/.test(formatQty(qty, precision));
  return `${positive ? '+' : ''}${written}`;
}

// ── INV-07 §5.5 — the over-available warning (warns, never blocks) ──────────

export function exceedsAvailable(qty: string, available: string): boolean {
  const wanted = toMilliQty(qty);
  const have = toMilliQty(available);
  if (wanted === null || have === null) return false;
  return wanted > have;
}

/**
 * BR-INV-05-01's low test, on AVAILABLE — and BR-MD-03-12's escape hatch: a
 * reorder point of zero switches the check off rather than flagging every row.
 */
export function isBelowReorder(available: string, reorderPoint: string): boolean {
  const line = toMilliQty(reorderPoint);
  const have = toMilliQty(available);
  if (line === null || have === null || line <= 0n) return false;
  return have <= line;
}

/** shipped − received, floored at zero — the amber "Pending: n" under a line. */
export function pendingQty(shipped: string, received: string): string | null {
  if (received.trim() === '') return null;
  const out = toMilliQty(shipped);
  const back = toMilliQty(received);
  if (out === null || back === null) return null;
  const short = out - back;
  return short > 0n ? fromMilliQty(short) : null;
}
